import { AlertDescription, Alert as ShadAlert } from '@neo/primitives/base/alert'
import { Sparkles, X } from 'lucide-react'
import { useState } from 'react'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'

const DISMISSED_STORAGE_KEY = 'integrations_migration_banner_dismissed'

const readDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_STORAGE_KEY) === 'true'
  } catch {
    // ignore storage read failures (private mode, etc.)
    return false
  }
}

interface IntegrationsMigrationBannerProps {
  /** Test/embedding override for the rollout gate; defaults to the flag. */
  aciEnabled?: boolean
}

/**
 * Announces the expanded tool collection and custom MCP servers on the agent
 * Tools tab. Only meaningful once the org is on the new service, so it is gated
 * on the rollout flag; the dismissal sticks across sessions so a one-time
 * announcement does not come back on every visit.
 */
export const IntegrationsMigrationBanner = ({
  aciEnabled: aciEnabledOverride,
}: IntegrationsMigrationBannerProps = {}) => {
  const { useAciIntegrations } = useIntegrationRollout()
  const aciEnabled = aciEnabledOverride ?? useAciIntegrations
  const [isDismissed, setIsDismissed] = useState(readDismissed)

  if (!aciEnabled || isDismissed) return null

  const handleDismiss = () => {
    setIsDismissed(true)
    try {
      localStorage.setItem(DISMISSED_STORAGE_KEY, 'true')
    } catch {
      // ignore storage write failures (private mode, etc.)
    }
  }

  return (
    <div className="relative mb-4" data-testid="banner-integrations-migration">
      <ShadAlert variant="info" className="items-center py-2 pr-10">
        <Sparkles size={16} />
        <AlertDescription className="text-xs-400">
          A richer tool collection is live — and you can now bring your own with custom MCP servers.
          Explore what&apos;s new and take your agents further.
        </AlertDescription>
      </ShadAlert>
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
        aria-label="Dismiss"
        data-testid="button-dismiss-integrations-migration-banner"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
